import React, { useEffect, useState, useMemo } from 'react'
import axios from 'axios'
import { Virtuoso } from 'react-virtuoso'

import {
  IconButton,
  Tooltip,
  Typography,
  Chip,
  Select,
  MenuItem,
  Container,
  Button,
  Box,
  TextField,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material'

import {
  DeleteOutlined as DeleteOutlinedIcon,
} from '@mui/icons-material/'

import { useAuthContext } from '../../../hooks/useAuthContext'
import { adminTeamsService } from '../../../services/admin/teams'

const SOURCE_TYPE = {
  LIST: 'list',
  TEAM: 'team',
}

// Simple debounce hook for search inputs
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

const ListDataDependencyManagement = () => {
  const { user } = useAuthContext()

  // State variables
  const [lists, setLists] = useState([])
  const [parentListId, setParentListId] = useState(-1)
  const [childListId, setChildListId] = useState(-1)

  const [parentListData, setParentListData] = useState([])
  const [childListData, setChildListData] = useState([])
  const [lddm, setLddm] = useState([])

  const [parentSourceType, setParentSourceType] = useState(SOURCE_TYPE.LIST)
  const [childSourceType, setChildSourceType] = useState(SOURCE_TYPE.LIST)
  const [teams, setTeams] = useState([])

  const [isInitialLoading, setIsInitialLoading] = useState(false)
  const [isMappingLoading, setIsMappingLoading] = useState(false)

  // Search queries & debounce
  const [parentSearchQuery, setParentSearchQuery] = useState('')
  const debouncedParentSearchQuery = useDebouncedValue(parentSearchQuery, 200)

  const [linkedSearch, setLinkedSearch] = useState('')
  const debouncedLinkedSearch = useDebouncedValue(linkedSearch, 200)

  const [availableSearch, setAvailableSearch] = useState('')
  const debouncedAvailableSearch = useDebouncedValue(availableSearch, 200)

  const [unassignedSearch, setUnassignedSearch] = useState('')
  const debouncedUnassignedSearch = useDebouncedValue(unassignedSearch, 200)

  // Active selection
  const [selectedParentId, setSelectedParentId] = useState(null)
  const [activeTab, setActiveTab] = useState(0) // 0 = Available, 1 = Unassigned

  const axiosOptions = useMemo(() => {
    if (user) {
      return { headers: { Authorization: `Bearer ${user.token}` } }
    }
    return {}
  }, [user])

  // Initial data loading effect (lists and teams) to avoid setState in effect warnings
  useEffect(() => {
    if (!user) return

    const loadInitialData = async () => {
      setIsInitialLoading(true)
      try {
        const response = await axios.get(`${NODE_BASE_URL}/template/list/company`, axiosOptions)
        setLists(response.data || [])
      } catch (err) {
        console.error('Failed to load lists metadata', err)
      }

      try {
        const data = await adminTeamsService.getTeams(user)
        setTeams(data || [])
      } catch (err) {
        console.error('Failed to load teams', err)
      } finally {
        setIsInitialLoading(false)
      }
    }

    loadInitialData()
  }, [user, axiosOptions])

  // Mapping data loading effect (list data and relationships) to avoid setState in effect warnings
  useEffect(() => {
    if (!user) return

    const loadMappingData = async () => {
      setIsMappingLoading(true)
      try {
        if (parentListId !== -1) {
          const response = await axios.get(`${NODE_BASE_URL}/template/list-data/list/${parentListId}`, axiosOptions)
          setParentListData(response.data || [])
        } else {
          setParentListData([])
        }

        if (childListId !== -1) {
          const response = await axios.get(`${NODE_BASE_URL}/template/list-data/list/${childListId}`, axiosOptions)
          setChildListData(response.data || [])
        } else {
          setChildListData([])
        }
      } catch (err) {
        console.error('Failed to load list item data', err)
      }

      try {
        const response = await axios.get(
          `${NODE_BASE_URL}/template/list-data/lddm-by-parent-list-id/${parentListId}`,
          axiosOptions
        )
        const fetchedLddm = response.data || []
        setLddm([...fetchedLddm].reverse())
      } catch (err) {
        console.error('Failed to load LDDM records', err)
      } finally {
        setIsMappingLoading(false)
      }
    }

    loadMappingData()
  }, [user, parentListId, childListId, axiosOptions])

  async function handleDeleteSingleLddm(pLdId, cLdId) {
    if (pLdId == null || cLdId == null) return
    const oldLddm = [...lddm]
    try {
      setLddm(
        lddm.filter((lddmItem) => !(lddmItem.child_id === cLdId && lddmItem.parent_id === pLdId))
      )
      await axios.delete(
        `${NODE_BASE_URL}/admin/template/list/delete-single-lddm/${pLdId}/${cLdId}`,
        axiosOptions
      )
    } catch {
      setLddm(oldLddm)
    }
  }

  async function handleAddSingleLddm(pLdId, cLdId, parentType, childType) {
    if (pLdId == null || cLdId == null) return
    const oldLddm = [...lddm]
    try {
      setLddm([
        {
          child_id: cLdId,
          parent_id: pLdId,
          parent_list_type: parentType,
          child_list_type: childType,
        },
        ...lddm,
      ])
      await axios.post(
        `${NODE_BASE_URL}/admin/template/list/add-single-lddm`,
        {
          parent_list_data_id: pLdId,
          child_list_data_id: cLdId,
          parent_type: parentType,
          child_type: childType,
        },
        axiosOptions
      )
    } catch {
      setLddm(oldLddm)
    }
  }

  // Normalize children data: source type check
  const childData = useMemo(() => {
    return childSourceType === SOURCE_TYPE.TEAM
      ? teams.map((t) => ({ ...t, value: t.name }))
      : childListData
  }, [childSourceType, teams, childListData])

  // Memoized search on Parent Items
  const filteredParentItems = useMemo(() => {
    const base = parentSourceType === SOURCE_TYPE.TEAM ? teams : parentListData
    if (!base) return []
    const sorted = [...base]
    if (parentSourceType === SOURCE_TYPE.TEAM) {
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    } else {
      sorted.sort((a, b) => (a.value || '').localeCompare(b.value || ''))
    }

    if (!debouncedParentSearchQuery) return sorted
    const q = debouncedParentSearchQuery.toLowerCase()
    return sorted.filter((item) => {
      const text = parentSourceType === SOURCE_TYPE.TEAM ? item.name : item.value
      return text && text.toLowerCase().includes(q)
    })
  }, [parentSourceType, teams, parentListData, debouncedParentSearchQuery])

  // Precomputed maps for O(1) relationships lookups
  const parentToChildIdsMap = useMemo(() => {
    const map = new Map()
    for (const item of lddm) {
      if (!map.has(item.parent_id)) {
        map.set(item.parent_id, new Set())
      }
      map.get(item.parent_id).add(item.child_id)
    }
    return map
  }, [lddm])

  const childToParentsMap = useMemo(() => {
    const map = new Map()
    const parentListMap = new Map((parentListData || []).map((p) => [p.id, p]))
    const teamMap = new Map((teams || []).map((t) => [t.id, t]))

    for (const item of lddm) {
      if (!map.has(item.child_id)) {
        map.set(item.child_id, [])
      }
      let parentObj = null
      if (item.parent_list_type === SOURCE_TYPE.LIST) {
        parentObj = parentListMap.get(item.parent_id)
      } else if (item.parent_list_type === SOURCE_TYPE.TEAM) {
        parentObj = teamMap.get(item.parent_id)
      }
      if (parentObj) {
        map.get(item.child_id).push(parentObj.value || parentObj.name)
      }
    }
    return map
  }, [lddm, parentListData, teams])

  const parentToLinkedChildrenMap = useMemo(() => {
    const map = new Map()
    const childMap = new Map((childData || []).map((c) => [c.id, c]))
    for (const [parentId, childIdsSet] of parentToChildIdsMap.entries()) {
      const list = []
      for (const cid of childIdsSet) {
        const childObj = childMap.get(cid)
        if (childObj) {
          list.push(childObj)
        }
      }
      map.set(parentId, list)
    }
    return map
  }, [parentToChildIdsMap, childData])

  // Get active parent information
  const activeParent = useMemo(() => {
    if (selectedParentId == null) return null
    return filteredParentItems.find((p) => p.id === selectedParentId)
  }, [selectedParentId, filteredParentItems])

  const activeParentName = useMemo(() => {
    if (!activeParent) return ''
    return parentSourceType === SOURCE_TYPE.TEAM ? activeParent.name : activeParent.value
  }, [activeParent, parentSourceType])

  // Computed data for selected parent
  const linkedChildren = useMemo(() => {
    if (selectedParentId == null) return []
    return parentToLinkedChildrenMap.get(selectedParentId) || []
  }, [selectedParentId, parentToLinkedChildrenMap])

  const sortedLinkedChildren = useMemo(() => {
    return linkedChildren
  }, [linkedChildren])

  const filteredLinkedChildren = useMemo(() => {
    if (!debouncedLinkedSearch) return sortedLinkedChildren
    const q = debouncedLinkedSearch.toLowerCase()
    return sortedLinkedChildren.filter((c) => c.value && c.value.toLowerCase().includes(q))
  }, [sortedLinkedChildren, debouncedLinkedSearch])

  // Available children to link
  const availableChildren = useMemo(() => {
    if (!childData) return []
    const linkedIds = parentToChildIdsMap.get(selectedParentId) || new Set()
    return childData.filter((c) => !linkedIds.has(c.id))
  }, [childData, selectedParentId, parentToChildIdsMap])

  const sortedAvailableChildren = useMemo(() => {
    return [...availableChildren].sort((a, b) => (a.value || '').localeCompare(b.value || ''))
  }, [availableChildren])

  const filteredAvailableChildren = useMemo(() => {
    if (!debouncedAvailableSearch) return sortedAvailableChildren
    const q = debouncedAvailableSearch.toLowerCase()
    return sortedAvailableChildren.filter((c) => c.value && c.value.toLowerCase().includes(q))
  }, [sortedAvailableChildren, debouncedAvailableSearch])

  // Unassigned children in the child list
  const unassignedChildren = useMemo(() => {
    if (!childData) return []
    const assignedIds = new Set(lddm.map((item) => item.child_id))
    return childData.filter((c) => !assignedIds.has(c.id))
  }, [childData, lddm])

  const sortedUnassigned = useMemo(() => {
    return [...unassignedChildren].sort((a, b) => (a.value || '').localeCompare(b.value || ''))
  }, [unassignedChildren])

  const filteredUnassigned = useMemo(() => {
    if (!debouncedUnassignedSearch) return sortedUnassigned
    const q = debouncedUnassignedSearch.toLowerCase()
    return sortedUnassigned.filter((c) => c.value && c.value.toLowerCase().includes(q))
  }, [sortedUnassigned, debouncedUnassignedSearch])

  // Toggle child list selection options
  const isChildConfigured = useMemo(() => {
    return (childSourceType === SOURCE_TYPE.LIST && childListId !== -1) || childSourceType === SOURCE_TYPE.TEAM
  }, [childSourceType, childListId])

  return (
    <Container maxWidth="xl" sx={{ py: 0, height: '90vh', display: 'flex', flexDirection: 'column' }}>
      <Box mb={1} flexShrink={0}>
        <Typography variant="h6" color="textPrimary" fontWeight="700" >
          Parent-Child Relationship Manager
        </Typography>
        {/* <Typography variant="body2" color="textSecondary">
          Define dependencies between list items or team assignments.
        </Typography> */}
      </Box>

      <Box display="flex" gap={3} sx={{ flexGrow: 1, minHeight: 0 }}>
        {/* Left Pane: Master Parent Items Selector */}
        <Paper
          variant="outlined"
          sx={{
            width: '35%',
            display: 'flex',
            flexDirection: 'column',
            p: 1,
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
          }}
        >
          <Typography variant="subtitle2" fontWeight="600" color="textSecondary" mb={1.5}>
            Select Parent Source
          </Typography>

          <Select
            size="small"
            value={parentSourceType}
            onChange={(e) => {
              setParentSourceType(e.target.value)
              setParentListId(-1)
              setSelectedParentId(null)
              setParentSearchQuery('')
              setChildSourceType(SOURCE_TYPE.LIST)
              setChildListId(-1)
              setLinkedSearch('')
              setAvailableSearch('')
              setUnassignedSearch('')
            }}
            fullWidth
            sx={{ mb: 1.5 }}
          >
            <MenuItem value={SOURCE_TYPE.LIST}>Lists</MenuItem>
            <MenuItem value={SOURCE_TYPE.TEAM}>Teams</MenuItem>
          </Select>

          {parentSourceType === SOURCE_TYPE.LIST && (
            <Select
              size="small"
              value={parentListId}
              onChange={(e) => {
                setParentListId(e.target.value)
                setSelectedParentId(null)
                setParentSearchQuery('')
                setChildSourceType(SOURCE_TYPE.LIST)
                setChildListId(-1)
                setLinkedSearch('')
                setAvailableSearch('')
                setUnassignedSearch('')
              }}
              fullWidth
              sx={{ mb: 1.5 }}
            >
              <MenuItem value={-1}>&lt;Select Parent List&gt;</MenuItem>
              {lists
                .sort((a, b) => (a.list_name || '').localeCompare(b.list_name || ''))
                .map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.list_name}
                  </MenuItem>
                ))}
            </Select>
          )}

          {((parentSourceType === SOURCE_TYPE.LIST && parentListId !== -1) ||
            parentSourceType === SOURCE_TYPE.TEAM) && (
            <>
              <TextField
                placeholder="Search parent items..."
                size="small"
                value={parentSearchQuery}
                onChange={(e) => setParentSearchQuery(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
                {isInitialLoading ? (
                  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={1}>
                    <CircularProgress size={30} />
                    <Typography variant="body2" color="textSecondary">
                      Loading sources...
                    </Typography>
                  </Box>
                ) : filteredParentItems.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      No items found.
                    </Typography>
                  </Box>
                ) : (
                  <Virtuoso
                    data={filteredParentItems}
                    itemContent={(index, parent) => {
                      const id = parent.id
                      const name = parentSourceType === SOURCE_TYPE.TEAM ? parent.name : parent.value
                      const isSelected = selectedParentId === id
                      const linkedCount = parentToLinkedChildrenMap.get(id)?.length || 0

                      return (
                        <Box
                          key={id}
                          onClick={() => setSelectedParentId(id)}
                          sx={{
                            p: 1.25,
                            mb: 0.75,
                            cursor: 'pointer',
                            borderRadius: '8px',
                            borderLeft: '4px solid',
                            borderColor: isSelected ? 'primary.main' : 'transparent',
                            backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                            '&:hover': {
                              backgroundColor: isSelected ? '#eff6ff' : '#f9fafb',
                            },
                            transition: 'all 0.15s ease',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Tooltip title={name} placement="top-start" enterDelay={500}>
                            <Typography
                              variant="body2"
                              fontWeight={isSelected ? 600 : 500}
                              color={isSelected ? 'primary.main' : 'textPrimary'}
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1, flexGrow: 1 }}
                            >
                              {index + 1}. {name}
                            </Typography>
                          </Tooltip>
                          <Chip
                            label={`${linkedCount} linked`}
                            size="small"
                            variant={isSelected ? 'filled' : 'outlined'}
                            color={isSelected ? 'primary' : 'default'}
                            sx={{ fontSize: '10px', height: '18px' }}
                          />
                        </Box>
                      )
                    }}
                  />
                )}
              </Box>
            </>
          )}
        </Paper>

        {/* Right Pane: Relationship Mapping configuration */}
        <Paper
          variant="outlined"
          sx={{
            width: '65%',
            display: 'flex',
            flexDirection: 'column',
            p: 1,
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
          }}
        >
          <Box mb={2} flexShrink={0}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="600">
                Configure Child Target
              </Typography>
              {selectedParentId != null && (
                <Typography variant="caption" color="textSecondary" fontWeight="600">
                  Mappings for: <span style={{ color: '#2563eb' }}>{activeParentName}</span>
                </Typography>
              )}
            </Box>
            <Box display="flex" gap={2} mt={1.5} alignItems="center">
              <Typography variant="body2" color="textSecondary" sx={{ minWidth: 90 }}>
                Child Source:
              </Typography>
              <Select
                size="small"
                value={childSourceType}
                onChange={(e) => {
                  setChildSourceType(e.target.value)
                  setChildListId(-1)
                  setLinkedSearch('')
                  setAvailableSearch('')
                  setUnassignedSearch('')
                }}
                sx={{ width: 120 }}
              >
                <MenuItem value={SOURCE_TYPE.LIST}>Lists</MenuItem>
                <MenuItem value={SOURCE_TYPE.TEAM}>Teams</MenuItem>
              </Select>

              {childSourceType === SOURCE_TYPE.LIST && (
                <Select
                  size="small"
                  value={childListId}
                  onChange={(e) => {
                    setChildListId(e.target.value)
                    setLinkedSearch('')
                    setAvailableSearch('')
                    setUnassignedSearch('')
                  }}
                  sx={{ flexGrow: 1 }}
                >
                  <MenuItem value={-1}>&lt;Select Child List&gt;</MenuItem>
                  {lists
                    .sort((a, b) => (a.list_name || '').localeCompare(b.list_name || ''))
                    .map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.list_name}
                      </MenuItem>
                    ))}
                </Select>
              )}
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {!isChildConfigured ? (
            <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
              <Typography color="textSecondary" variant="body2" sx={{ fontStyle: 'italic' }}>
                Please select a child list/team configuration to load mapping directories.
              </Typography>
            </Box>
          ) : isMappingLoading ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flexGrow={1} gap={1}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="textSecondary">
                Loading relationships...
              </Typography>
            </Box>
          ) : (
            <Box display="flex" gap={3} sx={{ flexGrow: 1, minHeight: 0 }}>
              {/* Left sub-column: Linked Children */}
              <Box display="flex" flexDirection="column" sx={{ width: '50%', minHeight: 0 }}>
                {selectedParentId == null ? (
                  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" p={2}>
                    <Typography color="textSecondary" variant="body2" align="center" sx={{ fontStyle: 'italic', maxWidth: 200 }}>
                      Select a parent item on the left to see linked children.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" fontWeight="600" color="textSecondary">
                        Linked Children ({filteredLinkedChildren.length})
                      </Typography>
                    </Box>
                    <TextField
                      placeholder="Search linked..."
                      size="small"
                      value={linkedSearch}
                      onChange={(e) => setLinkedSearch(e.target.value)}
                      fullWidth
                      sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                      {filteredLinkedChildren.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            No linked child items.
                          </Typography>
                        </Box>
                      ) : (
                        <Virtuoso
                          data={filteredLinkedChildren}
                          itemContent={(index, child) => {
                            const parentNames = childToParentsMap.get(child.id) || []
                            const isRepeated = parentNames.length > 1

                            const tooltipContent = isRepeated ? (
                              <div style={{ whiteSpace: 'pre-line' }}>
                                <strong>Item:</strong> {child.value}
                                <br />
                                <br />
                                <strong>Linked to multiple parents:</strong>
                                <br />
                                {parentNames.slice().sort().join(', ')}
                              </div>
                            ) : (
                              child.value
                            )

                            return (
                              <Box
                                key={child.id}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1,
                                  mb: 0.5,
                                  borderRadius: '6px',
                                  backgroundColor: isRepeated ? '#fef3c7' : '#f9fafb',
                                  border: '1px solid',
                                  borderColor: isRepeated ? '#fcd34d' : '#f3f4f6',
                                }}
                              >
                                <Tooltip title={tooltipContent} placement="top-start" enterDelay={500}>
                                  <Typography
                                    variant="caption"
                                    fontWeight={500}
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1, flexGrow: 1 }}
                                  >
                                    {index + 1}. {child.value}
                                  </Typography>
                                </Tooltip>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteSingleLddm(selectedParentId, child.id)}
                                  title="Remove mapping"
                                >
                                  <DeleteOutlinedIcon sx={{ fontSize: '15px' }} />
                                </IconButton>
                              </Box>
                            )
                          }}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Box>

              <Divider orientation="vertical" flexItem />

              {/* Right sub-column: Available / Unassigned to Link */}
              <Box display="flex" flexDirection="column" sx={{ width: '50%', minHeight: 0 }}>
                <Box display="flex" mb={1} gap={1} flexShrink={0}>
                  <Button
                    variant={activeTab === 0 ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setActiveTab(0)}
                    sx={{ textTransform: 'none', borderRadius: '20px', fontSize: '10px', px: 1.5, py: 0.25 }}
                  >
                    Available ({selectedParentId == null ? 0 : filteredAvailableChildren.length})
                  </Button>
                  <Button
                    variant={activeTab === 1 ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setActiveTab(1)}
                    sx={{ textTransform: 'none', borderRadius: '20px', fontSize: '10px', px: 1.5, py: 0.25 }}
                  >
                    Unassigned ({filteredUnassigned.length})
                  </Button>
                </Box>

                {activeTab === 0 ? (
                  <Box display="flex" flexDirection="column" sx={{ flexGrow: 1, minHeight: 0 }}>
                    {selectedParentId == null ? (
                      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" p={2}>
                        <Typography color="textSecondary" variant="body2" align="center" sx={{ fontStyle: 'italic', maxWidth: 200 }}>
                          Select a parent item on the left to see available children.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <TextField
                          placeholder="Search available..."
                          size="small"
                          value={availableSearch}
                          onChange={(e) => setAvailableSearch(e.target.value)}
                          fullWidth
                          sx={{ mb: 1.5 }}
                        />
                        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                          {filteredAvailableChildren.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                              <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                No available items found.
                              </Typography>
                            </Box>
                          ) : (
                            <Virtuoso
                              data={filteredAvailableChildren}
                              itemContent={(index, child) => (
                                <Box
                                  key={child.id}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 1,
                                    mb: 0.5,
                                    borderRadius: '6px',
                                    backgroundColor: '#f9fafb',
                                    border: '1px solid #f3f4f6',
                                  }}
                                >
                                  <Tooltip title={child.value} placement="top-start" enterDelay={500}>
                                    <Typography
                                      variant="caption"
                                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1, flexGrow: 1 }}
                                    >
                                      {index + 1}. {child.value}
                                    </Typography>
                                  </Tooltip>
                                  <Button
                                    color="primary"
                                    size="small"
                                    variant="contained"
                                    onClick={() =>
                                      handleAddSingleLddm(
                                        selectedParentId,
                                        child.id,
                                        parentSourceType,
                                        childSourceType
                                      )
                                    }
                                    sx={{ minWidth: 44, height: 22, fontSize: '10px', p: '0 8px', textTransform: 'none' }}
                                  >
                                    Link
                                  </Button>
                                </Box>
                              )}
                            />
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="column" sx={{ flexGrow: 1, minHeight: 0 }}>
                    <TextField
                      placeholder="Search unassigned..."
                      size="small"
                      value={unassignedSearch}
                      onChange={(e) => setUnassignedSearch(e.target.value)}
                      fullWidth
                      sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                      {filteredUnassigned.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            No unassigned items found.
                          </Typography>
                        </Box>
                      ) : (
                        <Virtuoso
                          data={filteredUnassigned}
                          itemContent={(index, child) => (
                            <Box
                              key={child.id}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1,
                                mb: 0.5,
                                borderRadius: '6px',
                                backgroundColor: '#f9fafb',
                                border: '1px solid #f3f4f6',
                              }}
                            >
                              <Tooltip title={child.value} placement="top-start" enterDelay={500}>
                                <Typography
                                  variant="caption"
                                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1, flexGrow: 1 }}
                                >
                                  {index + 1}. {child.value}
                                </Typography>
                              </Tooltip>
                              <Button
                                color="primary"
                                size="small"
                                variant="contained"
                                disabled={selectedParentId == null}
                                onClick={() =>
                                  handleAddSingleLddm(
                                    selectedParentId,
                                    child.id,
                                    parentSourceType,
                                    childSourceType
                                  )
                                }
                                sx={{ minWidth: 44, height: 22, fontSize: '10px', p: '0 8px', textTransform: 'none' }}
                              >
                                Link
                              </Button>
                            </Box>
                          )}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  )
}

export default ListDataDependencyManagement